import React, { useState } from 'react';
import { DashboardVersion, Period } from './vibrancyDashboardService';
import { VibrancyDashboardProvider, useVibrancy } from './VibrancyDashboardContext';

import DashboardHeader from './components/DashboardHeader';
import SelectorBar from './components/SelectorBar';
import GroupOverviewBanner from './components/GroupOverviewBanner';
import CompareBanner from './components/CompareBanner';
// import HighlightCard from './components/HighlightCard'; // VD-02 hidden pending clarification
import VibrancyPoints from './components/VibrancyPoints';
import WatchlistCards from './components/WatchlistCards';
import ProfileOverviewStrip from './components/ProfileOverviewStrip';
import BatchBifurcation from './components/BatchBifurcation';
import LocationAndStudent from './components/LocationAndStudent';
import AcademicFootprint from './components/AcademicFootprint';
import NotableAlumni from './components/NotableAlumni';
import OutreachStats from './components/OutreachStats';
import ActivitySectionBridge from './components/ActivitySectionBridge';
import { SectionDivider } from './components/shared';

const accordionSectionIds = [
    'vibrancyPoints',
    'dataCoverage',
    'outreachStats',
    'activityStats',
] as const;

type AccordionSectionId = typeof accordionSectionIds[number];

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
    { value: 'monthly',    label: 'Monthly' },
    { value: 'quarterly',  label: 'Quarterly' },
    { value: 'halfyearly', label: 'Half Yearly' },
    { value: 'annual',     label: 'Annual' },
];

/** Controlled period selector — each section owns its own value/onChange,
 *  so changing one section's period does NOT affect the others. */
const PeriodSelect: React.FC<{ value: Period; onChange: (p: Period) => void }> = ({ value, onChange }) => (
    <select
        className='form-select form-select-sm h-auto rounded-pill max-150'
        value={value}
        onChange={(e) => onChange(e.target.value as Period)}
    >
        {PERIOD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
        ))}
    </select>
);

const DashboardContent: React.FC = () => {
    const { version, instituteLoaded, loading, data, timeframe } = useVibrancy();
    // Per-section period — independent per accordion section.
    const [periods, setPeriods] = useState<Record<AccordionSectionId, Period>>({
        vibrancyPoints: timeframe,
        dataCoverage:   timeframe,
        outreachStats:  timeframe,
        activityStats:  timeframe,
    });
    const setPeriod = (id: AccordionSectionId, p: Period) =>
        setPeriods((prev) => ({ ...prev, [id]: p }));
    const isClient = version === 'client';
    const accordionId = 'vibrancyDashboardAccordion';
    const [expandedSections, setExpandedSections] = useState<AccordionSectionId[]>(['vibrancyPoints']);
    const isSectionExpanded = (sectionId: AccordionSectionId) => expandedSections.includes(sectionId);
    const areAllSectionsCollapsed = expandedSections.length === 0;
    const areAllSectionsExpanded = expandedSections.length === accordionSectionIds.length;
    const toggleSection = (sectionId: AccordionSectionId) => {
        setExpandedSections((current) =>
            current.includes(sectionId)
                ? current.filter((id) => id !== sectionId)
                : [...current, sectionId]
        );
    };
    const collapseAllSections = () => setExpandedSections([]);
    const expandAllSections = () => setExpandedSections([...accordionSectionIds]);

    return (
        <div className="main flex-1 bg-lighter-blue p-3 p-md-4">
            <div className="max-1140 mx-auto">
                <DashboardHeader />
                <SelectorBar />
                <GroupOverviewBanner />
                <CompareBanner />

                {!instituteLoaded ? (
                    <div className="text-center py-5 text-muted">
                        <i className="bi bi-buildings fs-1 d-block mb-3 opacity-25"></i>
                        <p className="font-16">{isClient ? 'Loading your institute...' : 'Select an institute to load the Vibrancy Dashboard'}</p>
                    </div>
                ) : loading || !data ? (
                    <div className="text-center py-5 text-muted">
                        <span className="spinner-border spinner-border-sm me-2"></span>Loading dashboard...
                    </div>
                ) : (
                    <>
                        {/* Highlight of the Period (VD-02) hidden pending clarification (UAD-04 AI spec + persistence). Restore when ready. */}
                        {/* <HighlightCard /> */}
                        <div className="row g-3 justify-content-end mb-3">
                            <div className="col-auto">
                                <button
                                    className={`btn ${areAllSectionsCollapsed ? 'btn-green' : 'btn-outline-secondary rounded-pill'}`}
                                    type="button"
                                    onClick={collapseAllSections}
                                >
                                    <span><i className="bi bi-arrows-collapse me-1"></i> Collapse </span>
                                </button>
                            </div>
                            <div className="col-auto">
                                <button
                                    className={`btn ${areAllSectionsExpanded ? 'btn-green' : 'btn-outline-secondary rounded-pill'}`}
                                    type="button"
                                    onClick={expandAllSections}
                                >
                                    <span><i className="bi bi-arrows-expand me-1"></i> Expand</span>
                                </button>
                            </div>
                        </div>

                        <div className="accordion dashboardAccordion" id={accordionId}>
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="vibrancyPointsHeading">
                                    <button
                                        className={`accordion-button font-18 ps-3 ${isSectionExpanded('vibrancyPoints') ? '' : 'collapsed'}`}
                                        type="button"
                                        onClick={() => toggleSection('vibrancyPoints')}
                                        aria-expanded={isSectionExpanded('vibrancyPoints')}
                                        aria-controls="vibrancyPointsCollapse"
                                    >
                                        ⚡5 Engaged Users

                                        <span className="badge  font-13 fw-medium rounded-pill ms-2">ranked by activity count · institute-wise</span>
                                    </button>
                                </h2>
                                <div
                                    id="vibrancyPointsCollapse"
                                    className={`accordion-collapse collapse ${isSectionExpanded('vibrancyPoints') ? 'show' : ''}`}
                                    aria-labelledby="vibrancyPointsHeading"
                                >
                                    <div className="accordion-body pt-4">
                                        <div className='position-sticky top-20 z-2 d-flex justify-content-end align-items-center gap-2 mb-3' >
                                            <div className='bg-white rounded-1 px-2 py-2 border border-1 shadow-sm font-13 text-secondary d-flex align-items-center gap-2'>
                                                <span className='me-1 font-500'>Period:</span>
                                                <PeriodSelect value={periods.vibrancyPoints} onChange={(p) => setPeriod("vibrancyPoints", p)} />
                                            </div>
                                        </div>

                                        <VibrancyPoints period={periods.vibrancyPoints} />
                                        <WatchlistCards period={periods.vibrancyPoints} />
                                    </div>
                                </div>
                            </div>

                            <div className="accordion-item">
                                <h2 className="accordion-header" id="dataCoverageHeading">
                                    <button
                                        className={`accordion-button font-18 ps-3 ${isSectionExpanded('dataCoverage') ? '' : 'collapsed'}`}
                                        type="button"
                                        onClick={() => toggleSection('dataCoverage')}
                                        aria-expanded={isSectionExpanded('dataCoverage')}
                                        aria-controls="dataCoverageCollapse"
                                    >
                                        📊 Data Coverage & Depth
                                        <span className="badge font-13 fw-medium rounded-pill ms-2">profile completeness, academic & professional footprint</span>
                                    </button>
                                </h2>
                                <div
                                    id="dataCoverageCollapse"
                                    className={`accordion-collapse collapse ${isSectionExpanded('dataCoverage') ? 'show' : ''}`}
                                    aria-labelledby="dataCoverageHeading"
                                >
                                    <div className="accordion-body pt-4">
                                        {/* <SectionDivider
                                            icon={'\uD83D\uDCCA'}
                                            title="Data Coverage & Depth"
                                            note="profile completeness, academic & professional footprint"
                                        /> */}
                                        <div className='position-sticky top-20 z-2 d-flex justify-content-end align-items-center gap-2 mb-3' >
                 
                                            <div className='bg-white rounded-1 px-2 py-2 border border-1 shadow-sm font-13 text-secondary d-flex align-items-center gap-2'>
                                                <span className='me-1 font-500'>Period:</span>
                                                <PeriodSelect value={periods.dataCoverage} onChange={(p) => setPeriod("dataCoverage", p)} />
                                            </div>
                                        </div>

                                        <ProfileOverviewStrip period={periods.dataCoverage} />
                                        <BatchBifurcation />
                                        <LocationAndStudent />
                                        <AcademicFootprint />
                                        <NotableAlumni />
                                    </div>
                                </div>
                            </div>

                            <div className="accordion-item">
                                <h2 className="accordion-header" id="outreachStatsHeading">
                                    <button
                                        className={`accordion-button font-18 ps-3 ${isSectionExpanded('outreachStats') ? '' : 'collapsed'}`}
                                        type="button"
                                        onClick={() => toggleSection('outreachStats')}
                                        aria-expanded={isSectionExpanded('outreachStats')}
                                        aria-controls="outreachStatsCollapse"
                                    >
                                        📞 Outreach Stats & Metrics
                                        <span className="badge font-13 fw-medium rounded-pill ms-2">univariety team outreach to alumni — client-safe view (no internal team breakdown)</span>
                                    </button>
                                </h2>
                                <div
                                    id="outreachStatsCollapse"
                                    className={`accordion-collapse collapse ${isSectionExpanded('outreachStats') ? 'show' : ''}`}
                                    aria-labelledby="outreachStatsHeading"
                                >
                                    <div className="accordion-body pt-4">
                                        <div className='position-sticky top-20 z-2 d-flex justify-content-end align-items-center gap-2 mb-3' >            
                                                <div className='bg-white rounded-1 px-2 py-2 border border-1 shadow-sm font-13 text-secondary d-flex align-items-center gap-2'>
                                                    <span className='me-1 font-500'>Period:</span>
                                                    <PeriodSelect value={periods.outreachStats} onChange={(p) => setPeriod("outreachStats", p)} />
                                                </div>
                                            </div>

                                        <OutreachStats period={periods.outreachStats} />
                                        <span className="badge bg-warning bg-opacity-10 border border-warning border-opacity-25 font-13 fw-medium text-warning-emphasis rounded-pill ms-2">univariety team outreach to alumni — client-safe view (no internal team breakdown)</span>
                                    </div>
                                </div> 
                            </div>

                            <div className="accordion-item">
                                <h2 className="accordion-header" id="activityStatsHeading">
                                    <button
                                        className={`accordion-button font-18 ps-3 ${isSectionExpanded('activityStats') ? '' : 'collapsed'}`}
                                        type="button"
                                        onClick={() => toggleSection('activityStats')}
                                        aria-expanded={isSectionExpanded('activityStats')}
                                        aria-controls="activityStatsCollapse"
                                    >
                                        ⚡Activity Stats & Metrics
                                        <span className="badge font-13 fw-medium rounded-pill ms-2">alumni & student self-driven actions — campaigns, platform, events</span>
                                    </button>
                                </h2>
                                <div
                                    id="activityStatsCollapse"
                                    className={`accordion-collapse collapse ${isSectionExpanded('activityStats') ? 'show' : ''}`}
                                    aria-labelledby="activityStatsHeading"
                                >
                                    <div className="accordion-body pt-4">
                                        <div className='position-sticky top-20 z-2 d-flex justify-content-end align-items-center gap-2 mb-3' >
                                            <div className='bg-white rounded-1 px-2 py-2 border border-1 shadow-sm font-13 text-secondary d-flex align-items-center gap-2'>
                                                <span className='me-1 font-500'>Period:</span>
                                                <PeriodSelect value={periods.activityStats} onChange={(p) => setPeriod("activityStats", p)} />
                                            </div>
                                        </div>

                                        <ActivitySectionBridge period={periods.activityStats} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

interface Props { version?: DashboardVersion; }

const VibrancyDashboard: React.FC<Props> = ({ version = 'sm' }) => (
    <VibrancyDashboardProvider version={version}>
        <DashboardContent />
    </VibrancyDashboardProvider>
);

export default VibrancyDashboard;
